// ---------------------------------------------------------------------------
// FlipClass — custom Bootstrap 5 front-end glue.
// Bundled + minified by esbuild (build.mjs) -> ../assets/js/app.min.js and loaded
// by base_bs5.django.html after the Bootstrap bundle + theme runtime.
//
// Home for small, cross-page behaviours. Heavier page-specific libraries
// (flatpickr, glightbox, bs-stepper, …) can be `import`ed here (esbuild bundles
// them) or split into per-page entries.
//
// Bootstrap's JS is the vendor bundle (assets/libs/bootstrap/.../bootstrap.bundle.min.js),
// exposing the global `window.bootstrap`. We use that global rather than re-importing it.
// ---------------------------------------------------------------------------

import { html5Adapter, trackProgress, vimeoAdapter } from './video-progress.js'

function getCookie(name) {
  const m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')
  return m ? m.pop() : ''
}

function initBootstrapComponents() {
  const bs = window.bootstrap
  if (!bs) return
  document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
    bs.Tooltip.getOrCreateInstance(el)
  })
  document.querySelectorAll('[data-bs-toggle="popover"]').forEach((el) => {
    bs.Popover.getOrCreateInstance(el)
  })
}

// Declarative progress bars for the learning environment.
// Markup: an element with [data-progress-bar] containing a `.progress > .progress-bar`.
//   - data-progress      : server-rendered percentage (0-100), shown immediately.
//   - data-progress-url  : optional endpoint returning {progress: <0-100>}; refreshes the bar.
//   - a sibling/descendant `.progress-check` icon turns "complete" at >= 95%.
// Replaces the legacy progressbar.js + App.init() auto-initialiser.
function setBar(container, pct) {
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0))
  const bar = container.querySelector('.progress-bar')
  if (bar) {
    bar.style.width = clamped + '%'
    bar.setAttribute('aria-valuenow', String(Math.round(clamped)))
    if (bar.dataset.showValue !== 'false') bar.textContent = Math.round(clamped) + '%'
  }
  const check = container.querySelector('.progress-check')
  if (check && clamped >= 95) {
    check.classList.remove('text-muted', 'text-secondary')
    check.classList.add('text-success')
  }
}

function initProgressBars() {
  document.querySelectorAll('[data-progress-bar]').forEach((el) => {
    // data-progress is Django's |progress:user output — a 0..1 FRACTION — so scale to a percent.
    if (el.dataset.progress != null && el.dataset.progress !== '') {
      setBar(el, parseFloat(el.dataset.progress) * 100)
    }
    // data-progress-url returns {progress: <0..100 percent>} (flipclass_api).
    const url = el.dataset.progressUrl
    if (!url) return
    fetch(url, { credentials: 'same-origin', headers: { 'X-CSRFToken': getCookie('csrftoken') } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.progress != null) setBar(el, d.progress) })
      .catch(() => {})
  })
}

// Video players ([data-video-progress], parts/video-player.html): save where the viewer is and resume there.
function sendProgress(url, body, keepalive) {
  return fetch(url, {
    method: 'PUT',
    credentials: 'same-origin',
    keepalive,
    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCookie('csrftoken') },
    body: JSON.stringify(body),
  }).then((r) => { if (!r.ok) throw new Error('Saving video progress failed: ' + r.status) })
}

function initVideoPlayers() {
  document.querySelectorAll('[data-video-progress]').forEach((el) => {
    let player
    if (el.dataset.videoProgress === 'vimeo') {
      const iframe = el.querySelector('iframe')
      if (!iframe || !window.Vimeo) return // player.js blocked or failed to load
      player = vimeoAdapter(new window.Vimeo.Player(iframe))
    } else {
      const video = el.querySelector('video')
      if (!video) return
      player = html5Adapter(video)
    }
    const tracker = trackProgress(player, {
      url: el.dataset.progressUrl,
      resumeAt: parseInt(el.dataset.resumeAt, 10) || 0,
      send: sendProgress,
    })
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') tracker.leave() })
    window.addEventListener('pagehide', tracker.leave)
  })
}

// HTMX: send Django's CSRF token on every htmx request (POST/PUT/DELETE etc.).
document.body.addEventListener('htmx:configRequest', (evt) => {
  evt.detail.headers['X-CSRFToken'] = getCookie('csrftoken')
})

// HTMX: replace the native confirm() dialog (hx-confirm) with a proper Bootstrap 5
// modal (#fc-confirm-modal, defined in base_bs5). Any element with hx-confirm gets it.
document.body.addEventListener('htmx:confirm', (evt) => {
  const question = evt.detail.question
  if (!question) return // no hx-confirm on this element -> let htmx proceed normally
  const bs = window.bootstrap
  const modalEl = document.getElementById('fc-confirm-modal')
  const okBtn = modalEl && modalEl.querySelector('[data-fc-confirm-ok]')
  const msgEl = modalEl && modalEl.querySelector('[data-fc-confirm-message]')
  if (!bs || !modalEl || !okBtn) return // fall back to htmx's native confirm
  evt.preventDefault() // stop the request; we'll issue it if confirmed
  if (msgEl) msgEl.textContent = question
  const modal = bs.Modal.getOrCreateInstance(modalEl)
  const onOk = () => { cleanup(); modal.hide(); evt.detail.issueRequest(true) }
  const onHidden = () => cleanup()
  function cleanup() {
    okBtn.removeEventListener('click', onOk)
    modalEl.removeEventListener('hidden.bs.modal', onHidden)
  }
  okBtn.addEventListener('click', onOk)
  modalEl.addEventListener('hidden.bs.modal', onHidden)
  modal.show()
})

// HTMX: toasts swapped in out of band (into #fc-toasts, base_bs5) are shown, then removed once hidden.
// The .fc-toast-progress bar counts down Bootstrap's autohide delay (data-bs-delay). Bootstrap holds the
// toast while it is hovered or focused and restarts the full delay afterwards, so the bar does the same.
function initToast(el) {
  const delay = Number(el.dataset.bsDelay) || 5000 // Bootstrap's default
  el.style.setProperty('--fc-toast-delay', delay + 'ms')
  let hovered = false
  let focused = false
  const sync = () => {
    el.classList.remove('fc-toast-counting')
    if (hovered || focused) return
    void el.offsetWidth // restart the CSS animation
    el.classList.add('fc-toast-counting')
  }
  el.addEventListener('shown.bs.toast', sync)
  el.addEventListener('mouseenter', () => { hovered = true; sync() })
  el.addEventListener('mouseleave', () => { hovered = false; sync() })
  el.addEventListener('focusin', () => { focused = true; sync() })
  el.addEventListener('focusout', (e) => {
    if (el.contains(e.relatedTarget)) return
    focused = false
    sync()
  })
  el.addEventListener('hidden.bs.toast', () => el.remove())
  window.bootstrap.Toast.getOrCreateInstance(el).show()
}

document.body.addEventListener('htmx:load', (evt) => {
  const el = evt.detail.elt
  if (window.bootstrap && el instanceof Element && el.matches('[data-fc-toast]')) initToast(el)
})

// Course catalog ("Alle kurs og priser", /fag): [data-catalog] filters its cards in place — the category chip, the
// subject chip and the search — by the server's rules (courses.card_matches), and keeps the URL in step so a reload
// or Back from a course page shows the same list. Without this script the chips are links and the search a form.
function initCatalog(root) {
  const search = root.querySelector('[data-catalog-search]')
  const hiddenInAll = (root.dataset.catalogHiddenInAll || '').split(' ').filter(Boolean)
  const pressed = (name) => root.querySelector(`[data-catalog-chip="${name}"][aria-pressed="true"]`)
  const state = {
    kategori: pressed('kategori') ? pressed('kategori').dataset.value : 'alle',
    fag: pressed('fag') ? pressed('fag').dataset.value : '',
    q: search ? search.value : '',
  }

  const urlFor = (filter) => {
    const params = new URLSearchParams()
    if (filter.kategori && filter.kategori !== 'alle') params.set('kategori', filter.kategori)
    if (filter.fag) params.set('fag', filter.fag)
    if (filter.q.trim()) params.set('q', filter.q.trim())
    const query = params.toString()
    return root.dataset.catalogUrl + (query ? '?' + query : '')
  }

  const matches = (card, terms) => {
    const inCategory = state.kategori === 'alle'
      ? !hiddenInAll.includes(card.dataset.group) || terms.length > 0
      : card.dataset.categories.split(' ').includes(state.kategori)
    return inCategory
      && (!state.fag || card.dataset.subject === state.fag)
      && terms.every((term) => card.dataset.search.includes(term))
  }

  const apply = () => {
    const terms = state.q.toLowerCase().split(/\s+/).filter(Boolean)
    let anyVisible = false
    root.querySelectorAll('[data-catalog-group]').forEach((group) => {
      let count = 0
      group.querySelectorAll('[data-catalog-card]').forEach((card) => {
        const show = matches(card, terms)
        card.hidden = !show
        if (show) count += 1
      })
      group.hidden = count === 0
      group.querySelector('[data-catalog-group-count]').textContent = count
      anyVisible = anyVisible || count > 0
    })
    root.querySelector('[data-catalog-empty]').hidden = anyVisible
    root.querySelectorAll('[data-catalog-note]').forEach((note) => {
      note.hidden = note.dataset.catalogNote !== state.kategori
    })
    root.querySelectorAll('[data-catalog-chip]').forEach((chip) => {
      const name = chip.dataset.catalogChip
      const active = chip.dataset.value === state[name]
      const row = chip.closest('[data-active-class]')
      chip.classList.toggle(row.dataset.activeClass, active)
      chip.classList.toggle(row.dataset.inactiveClass, !active)
      chip.setAttribute('aria-pressed', String(active))
      chip.href = urlFor({ ...state, [name]: chip.dataset.value })
    })
    root.querySelectorAll('[data-catalog-filter] input[type="hidden"]').forEach((input) => {
      input.value = state[input.name]
    })
    history.replaceState(history.state, '', urlFor(state))
  }

  root.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-catalog-chip]')
    if (chip) {
      event.preventDefault()
      state[chip.dataset.catalogChip] = chip.dataset.value
      apply()
      // Scrolled down with the bar stuck: back to the top of the (shorter) list.
      const offset = parseFloat(root.style.getPropertyValue('--fc-navbar-height')) || 0
      const top = root.getBoundingClientRect().top
      if (top < offset) window.scrollTo({ top: window.scrollY + top - offset })
    } else if (event.target.closest('[data-catalog-reset]')) {
      event.preventDefault()
      Object.assign(state, { kategori: 'alle', fag: '', q: '' })
      if (search) search.value = ''
      apply()
    }
  })
  if (search) {
    search.addEventListener('input', () => {
      state.q = search.value
      apply()
    })
    search.form.addEventListener('submit', (event) => {
      event.preventDefault()
      search.blur() // closes the phone keyboard
    })
  }

  // On a phone the chip rows scroll sideways: bring the active chip into view.
  root.querySelectorAll('[data-catalog-chip][aria-pressed="true"]').forEach((chip) => {
    const row = chip.parentElement
    if (row.scrollWidth > row.clientWidth) row.scrollLeft = chip.offsetLeft - row.offsetLeft - 16
  })

  // The filter bar sticks below the sticky navbar.
  const navbar = document.querySelector('nav.navbar.sticky-top')
  if (navbar) {
    const setOffset = () => root.style.setProperty('--fc-navbar-height', navbar.offsetHeight + 'px')
    setOffset()
    window.addEventListener('resize', setOffset)
  }
}

// Formler og tabeller (the CMS pages Formelhefte i matematikk and Fysikktabeller): the search bar works as the /fag
// filter bar. The search box #fc-sok filters the [data-fc-card] tables in place (rows, or [data-fc-group] groups of
// [data-fc-item]), with "n treff"; the chips narrow it to an area (data-fc-chip="omrade", its data-fc-sections ids)
// and/or a subject (data-fc-chip="fag", matched against the card's badges, "1T · R1 · S1"). The URL keeps the filter
// in step (?omrade=&fag=&q=). [data-fc-jump] links and the #fc-velg list (older page markup an admin may have kept)
// scroll to a section with its heading below the sticky navbar and search bar.
function initFormulaSearch(search) {
  const hits = document.getElementById('fc-treff')
  const none = document.getElementById('fc-ingen')
  const bar = document.getElementById('fc-sokelinje')
  const select = document.getElementById('fc-velg')
  const navbar = document.querySelector('nav.navbar.sticky-top')
  const chips = [...document.querySelectorAll('[data-fc-chip]')]
  const cards = [...document.querySelectorAll('[data-fc-card]')]
  const norm = (text) => text.toLowerCase().replace(/\s+/g, ' ')
  const show = (el, visible) => el.classList.toggle('d-none', !visible)
  const chipFor = (name, value) => chips.find((chip) => chip.dataset.fcChip === name && chip.dataset.fcValue === value)

  const params = new URLSearchParams(window.location.search)
  const state = { omrade: '', fag: '' }
  Object.keys(state).forEach((name) => {
    if (params.get(name) && chipFor(name, params.get(name))) state[name] = params.get(name)
  })
  if (params.get('q')) search.value = params.get('q')

  const inChips = (card) => {
    const area = state.omrade && chipFor('omrade', state.omrade)
    if (area && !area.dataset.fcSections.split(' ').includes(card.id)) return false
    if (!state.fag) return true
    const subjects = [...card.querySelectorAll('.badge')].flatMap((badge) => badge.textContent.split('·'))
    return subjects.some((subject) => subject.trim() === state.fag)
  }

  const filter = () => {
    const query = norm(search.value.trim())
    const narrowed = Boolean(query || state.omrade || state.fag)
    let count = 0
    let visibleCards = 0
    cards.forEach((card) => {
      if (!inChips(card)) {
        show(card, false)
        return
      }
      const heading = card.querySelector('h2')
      const badge = card.querySelector('.badge')
      const title = norm(`${heading ? heading.textContent : ''} ${badge ? badge.textContent : ''}`)
      let anyVisible = false
      card.querySelectorAll('tbody tr').forEach((row) => {
        const match = !query || title.includes(query) || norm(row.textContent).includes(query)
        show(row, match)
        if (match) {
          anyVisible = true
          count += 1
        }
      })
      card.querySelectorAll('[data-fc-group]').forEach((group) => {
        const groupTitle = group.querySelector('[data-fc-gtitle]')
        const groupText = norm(groupTitle ? groupTitle.textContent : '')
        let groupVisible = false
        group.querySelectorAll('[data-fc-item]').forEach((item) => {
          const match = !query || title.includes(query) || groupText.includes(query)
            || norm(item.textContent).includes(query)
          show(item, match)
          if (match) {
            groupVisible = true
            count += 1
          }
        })
        show(group, groupVisible)
        anyVisible = anyVisible || groupVisible
      })
      show(card, anyVisible)
      if (anyVisible) visibleCards += 1
    })
    if (none) show(none, visibleCards === 0)
    if (hits) hits.textContent = narrowed ? `${count} treff` : ''

    chips.forEach((chip) => {
      const active = chip.dataset.fcValue === state[chip.dataset.fcChip]
      const row = chip.closest('[data-fc-active]')
      if (row) {
        chip.classList.toggle(row.dataset.fcActive, active)
        chip.classList.toggle(row.dataset.fcInactive, !active)
      }
      chip.setAttribute('aria-pressed', String(active))
    })
    const urlParams = new URLSearchParams()
    if (state.omrade) urlParams.set('omrade', state.omrade)
    if (state.fag) urlParams.set('fag', state.fag)
    if (search.value.trim()) urlParams.set('q', search.value.trim())
    const url = window.location.pathname + (urlParams.toString() ? `?${urlParams}` : '')
    history.replaceState(history.state, '', url)
  }

  // Below md the bar scrolls away (flipclass.css), so only sticky bars cover the page.
  const covered = () => {
    const height = (el) => (el && getComputedStyle(el).position === 'sticky' ? el.offsetHeight : 0)
    return height(navbar) + height(bar)
  }
  const scrollToTarget = (target, behavior) => {
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - covered() - 16, behavior })
  }

  const jump = (hash) => {
    const target = hash && hash.charAt(0) === '#' && document.getElementById(hash.slice(1))
    if (!target) return false
    search.value = ''
    Object.assign(state, { omrade: '', fag: '' })
    filter()
    scrollToTarget(target, 'smooth')
    history.replaceState(history.state, '', hash)
    return true
  }

  chips.forEach((chip) => {
    chip.addEventListener('click', () => {
      state[chip.dataset.fcChip] = chip.dataset.fcValue
      filter()
      // Scrolled down with the bar stuck: back to the top of the (shorter) list.
      const first = cards.find((card) => !card.classList.contains('d-none'))
      if (first && first.getBoundingClientRect().top < covered()) scrollToTarget(first, 'auto')
    })
  })
  search.addEventListener('input', filter)
  search.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') search.blur() // closes the phone keyboard
  })
  document.querySelectorAll('[data-fc-jump]').forEach((link) => {
    link.addEventListener('click', (event) => {
      if (jump(link.getAttribute('href'))) event.preventDefault()
    })
  })
  if (select) {
    select.addEventListener('change', () => {
      if (select.value) jump(select.value)
      select.value = ''
    })
  }
  // The search bar sticks below the sticky navbar.
  if (bar && navbar) {
    const setTop = () => { bar.style.top = navbar.offsetHeight + 'px' }
    setTop()
    window.addEventListener('resize', setTop)
  }
  if (state.omrade || state.fag || search.value) filter()
}

function onReady() {
  initBootstrapComponents()
  initProgressBars()
  initVideoPlayers()
  document.querySelectorAll('[data-catalog]').forEach(initCatalog)
  const formulaSearch = document.getElementById('fc-sok')
  if (formulaSearch) initFormulaSearch(formulaSearch)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', onReady)
} else {
  onReady()
}
// Refresh progress when returning to a cached page (matches the legacy pageshow behaviour).
window.addEventListener('pageshow', initProgressBars)

// Expose for pages that inject rows dynamically.
window.FlipClass = window.FlipClass || {}
window.FlipClass.initProgressBars = initProgressBars
window.FlipClass.getCookie = getCookie
